import { IconChevronLeft } from '@tabler/icons-react';

import { Button } from '../Button';
import styles from './_DialogFooter.module.css';

export interface DialogFooterProps {
  /** Left-aligned Back button label (tertiary + chevron-left) — omit to hide */
  backLabel?: string;
  /** Callback fired when the Back button is clicked */
  onBack?: () => void;
  /** Right-aligned secondary (tertiary) button label — omit to hide */
  secondaryLabel?: string;
  /** Callback fired when the secondary button is clicked */
  onSecondaryAction?: () => void;
  /** Whether the secondary button shows a loader */
  isSecondaryLoading?: boolean;
  /** Right-aligned primary CTA label */
  primaryLabel: string;
  /** Callback fired when the primary CTA is clicked */
  onPrimaryAction?: () => void;
  /** Whether the primary CTA shows a loader */
  isPrimaryLoading?: boolean;
  /** Whether the primary CTA is disabled */
  isPrimaryDisabled?: boolean;
  /** Primary button variant — 'negative' for destructive actions */
  primaryVariant?: 'primary' | 'negative';
}

/**
 * Private shared footer for ConfirmationDialog and ActionDialog.
 * Split layout: optional left Back button, right-aligned secondary + primary.
 * When Back is omitted the actions group stays right-aligned.
 */
export function DialogFooter({
  backLabel,
  onBack,
  secondaryLabel,
  onSecondaryAction,
  isSecondaryLoading,
  primaryLabel,
  onPrimaryAction,
  isPrimaryLoading,
  isPrimaryDisabled,
  primaryVariant = 'primary',
}: DialogFooterProps) {
  return (
    <div className={styles.footer}>
      {backLabel && (
        <Button
          variant="tertiary"
          leftSection={<IconChevronLeft size={20} stroke={2} />}
          onClick={onBack}
        >
          {backLabel}
        </Button>
      )}
      <div className={styles.actions}>
        {secondaryLabel && (
          <Button
            variant="tertiary"
            onClick={onSecondaryAction}
            loading={isSecondaryLoading}
          >
            {secondaryLabel}
          </Button>
        )}
        <Button
          variant={primaryVariant}
          onClick={onPrimaryAction}
          loading={isPrimaryLoading}
          disabled={isPrimaryDisabled}
        >
          {primaryLabel}
        </Button>
      </div>
    </div>
  );
}

DialogFooter.displayName = 'DialogFooter';
